/**
 * Lays seats around an oval table.  Game-agnostic — the parent passes a
 * render function that produces the seat overlay (nameplate + play zone)
 * for each seat, and the children prop renders the table center.
 *
 * Seats are positioned so the local player (`mySeatIndex`) is at the
 * bottom, partners across, etc.
 */

import type { CSSProperties, ReactNode } from "react";
import { useWindowWidth } from "../lib/uiHooks.js";

interface CircleTableProps {
  seatCount: number;
  /** Local player's seat. The view rotates so this seat is at the bottom. */
  mySeatIndex: number | null;
  renderSeat: (seatIndex: number, isMine: boolean) => ReactNode;
  children?: ReactNode;
}

/**
 * Returns CSS positions for each absolute-seat-index, after rotating so
 * `mySeatIndex` is at the bottom (180°). Angles are in degrees.
 *
 * `rxPct` controls how far the side seats sit from center. Phones get
 * a tighter ellipse so the per-seat overlay (nameplate + any card
 * strip) doesn't run off the edges of the rotated viewport.
 */
function seatPositions(
  seatCount: number,
  mySeatIndex: number | null,
  rxPct: number
): { angleDeg: number; left: string; top: string }[] {
  const out: { angleDeg: number; left: string; top: string }[] = [];
  for (let i = 0; i < seatCount; i++) {
    let displayIndex = i;
    if (mySeatIndex !== null) {
      displayIndex = (i - mySeatIndex + seatCount) % seatCount;
    }
    // displayIndex 0 = bottom. Spread the rest evenly counter-clockwise.
    const angleDeg = 90 + (displayIndex * 360) / seatCount;
    // Ellipse: x = cx + rx*cos(t), y = cy + ry*sin(t).
    const rad = (angleDeg * Math.PI) / 180;
    const cx = 50; // % of table width
    const cy = 50;
    const rx = rxPct; // % of table width
    const ry = 38;
    const left = `${cx + rx * Math.cos(rad)}%`;
    const top = `${cy + ry * Math.sin(rad)}%`;
    out.push({ angleDeg, left, top });
  }
  return out;
}

export function CircleTable({ seatCount, mySeatIndex, renderSeat, children }: CircleTableProps) {
  // On narrow viewports (phones, including the rotated-landscape phone
  // mode where `innerWidth` is still the device's portrait width) pull
  // the side seats inward by ~a card's width so per-seat overlays
  // don't spill past the screen edge.
  const windowWidth = useWindowWidth();
  const rxPct = windowWidth < 700 ? 31 : 42;
  const positions = seatPositions(seatCount, mySeatIndex, rxPct);
  return (
    <div className="table">
      {positions.map((p, i) => {
        const style: CSSProperties = { left: p.left, top: p.top };
        return (
          <div className="seat" key={i} style={style}>
            {renderSeat(i, i === mySeatIndex)}
          </div>
        );
      })}
      <div className="table-center">{children}</div>
    </div>
  );
}
